import type { VoidFunctionComponent } from 'react';
import { useSelector } from 'react-redux';

import { FormLabel, FormLabelVariant } from '../../generic';
import PackageLocatorFilter from './PackageLocatorFilter';
import type { WithResetSelectedPackage } from '../PackageInventoryContainer';
import * as PackageInventoryStore from '../../../redux/packageInventory';
import useFetchPackages from '../hooks/useFetchPackages';
import type { SelectOption } from '../../../types';
import usePackageManagers from '../hooks/usePackageManagers';
import type { Fetcher } from '../../../../shared/fetchers';
import PackageManager from './PackageManager';
import FilterCard from '../../generic/Filter/FilterCard';

type Props = {
  areFetchersLoading: boolean;
  fetcherOptions: Array<SelectOption<Fetcher>>;
  selectedFetchers: Array<SelectOption<Fetcher>>;
  selectedLocators: Array<SelectOption<string>>;
  setFilters: (...args: unknown[]) => void;
};

/**
 * PackageInventoryFiltersProject has filters related to filtering
 * packages by package manager (fetcher), labels, and locator.
 */
const PackageInventoryFiltersPackages: VoidFunctionComponent<Props> = ({
  areFetchersLoading,
  fetcherOptions,
  selectedFetchers,
  selectedLocators,
  setFilters,
}) => (
  <FilterCard id="package-inventory-package-filters" title="Package">
    <FormLabel label="Package manager" variant={FormLabelVariant.Filter}>
      <PackageManager
        isLoading={areFetchersLoading}
        fetcherOptions={fetcherOptions}
        selectedFetchers={selectedFetchers}
        setFilters={setFilters}
      />
    </FormLabel>
    {/* TODO(CORE-4566): Add filter for Package Labels. */}
    <FormLabel label="Locator" variant={FormLabelVariant.Filter}>
      <PackageLocatorFilter
        locators={selectedLocators}
        setFilters={setFilters}
      />
    </FormLabel>
  </FilterCard>
);

/**
 * Stateful container for the package filters as they are on the main /packages page.
 */
const PackageInventoryFiltersPackagesContainer: VoidFunctionComponent<{
  withResetSelectedPackage: WithResetSelectedPackage;
}> = ({
  withResetSelectedPackage,
}) => {
  const selectedLocators = useSelector(PackageInventoryStore.selectors.getFiltersLocatorsOptions);
  const selectedFetchers = useSelector(PackageInventoryStore.selectors.getFiltersFetchersOptions);
  const { fetchers: fetcherOptions, isLoading: areFetchersLoading } = usePackageManagers();
  const setFilters = useFetchPackages(PackageInventoryStore.actions.setFilters);
  return (
    <PackageInventoryFiltersPackages
      areFetchersLoading={areFetchersLoading}
      fetcherOptions={fetcherOptions}
      selectedFetchers={selectedFetchers}
      selectedLocators={selectedLocators}
      setFilters={withResetSelectedPackage(setFilters)}
    />
  );
};

export default PackageInventoryFiltersPackagesContainer;
